import { useEffect, useMemo, useRef, useState } from 'react';
import { layoutDocument, normalizeProfile, qrMatrixToBitmap } from '@shripos/shared/escpos';
import { cn } from '@shripos/ui';
import { logoBitmap, paintBitmap, qrMatrix } from '../lib/printing/raster.js';

function Bitmap({ bmp, dotWidth, align = 'center' }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && bmp) paintBitmap(ref.current, bmp);
  }, [bmp]);
  if (!bmp) return null;
  return (
    <div
      className={cn(
        'flex py-0.5',
        align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center',
      )}
    >
      <canvas ref={ref} style={{ width: `${(bmp.width / dotWidth) * 100}%`, imageRendering: 'pixelated' }} />
    </div>
  );
}

function Logo({ url, profile }) {
  const [bmp, setBmp] = useState(null);
  useEffect(() => {
    let alive = true;
    logoBitmap(url, profile)
      .then((b) => alive && setBmp(b))
      .catch(() => alive && setBmp(null));
    return () => {
      alive = false;
    };
  }, [url, profile]);
  return <Bitmap bmp={bmp} dotWidth={profile.dotWidth} />;
}

/**
 * What the thermal printer will print, row for row: it renders the same layout rows that the
 * ESC/POS encoder turns into bytes (fixed-width text, double width/height, inverted lines,
 * logo raster, QR). Used for the template editor and for browser printing of any document.
 */
export function ThermalPreview({ doc, profile: rawProfile, logoUrl, className }) {
  const profile = useMemo(() => normalizeProfile(rawProfile), [rawProfile]);
  const rows = useMemo(() => layoutDocument(doc, profile), [doc, profile]);
  const cpl = profile.charsPerLine;
  return (
    <div
      className={cn('thermal mx-auto bg-white px-3 py-4 font-mono text-black', className)}
      style={{
        width: `calc(${cpl}ch + 1.5rem)`,
        fontSize: profile.paperWidth === '58mm' ? '11px' : '12px',
        lineHeight: 1.35,
      }}
    >
      {rows.map((r, i) => {
        if (r.k === 'text') {
          return (
            <div key={i} style={{ height: `${1.35 * r.h}em` }} className="overflow-visible">
              <div
                className={cn(
                  'whitespace-pre',
                  r.bold && 'font-bold',
                  r.underline && 'underline',
                  r.invert && 'bg-black text-white',
                )}
                style={{
                  width: `${r.cols}ch`,
                  transform: r.w > 1 || r.h > 1 ? `scale(${r.w}, ${r.h})` : undefined,
                  transformOrigin: 'left top',
                }}
              >
                {r.text}
              </div>
            </div>
          );
        }
        if (r.k === 'image')
          return r.name === 'logo' && logoUrl ? <Logo key={i} url={logoUrl} profile={profile} /> : null;
        if (r.k === 'qr') {
          if (profile.qrMode === 'off') return null;
          const m = qrMatrix(r.data);
          return <Bitmap key={i} bmp={qrMatrixToBitmap(m, r.module)} dotWidth={profile.dotWidth} align={r.align} />;
        }
        if (r.k === 'barcode') {
          return (
            <div key={i} className="py-1 text-center">
              <div
                className="mx-auto h-10 w-3/4"
                style={{
                  background: 'repeating-linear-gradient(90deg,#000 0 2px,#fff 2px 3px,#000 3px 4px,#fff 4px 7px)',
                }}
              />
              <div>{r.data}</div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}
